import React, { useState, useEffect, useMemo } from 'react'
import {
  CogIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { scoreTemplate } from '../../utils/venueRiskAssessmentScorer'
import {
  QUESTION_TYPES,
  newDimension,
  newQuestion,
  newSelectOption,
  rebalanceDimensionWeights,
} from '../../utils/venueRiskAssessmentTemplateBuilder'

function weightSum(definition) {
  return (definition?.dimensions || []).reduce((s, d) => s + Number(d.weight || 0), 0)
}

function isScoredType(type) {
  return type === 'select' || type === 'boolean'
}

function typeLabel(type) {
  return QUESTION_TYPES.find((t) => t.value === type)?.label || type
}

function Modal({ open, onClose, title, children, footer }) {
  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'unset'
    return () => { document.body.style.overflow = 'unset' }
  }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 bg-white w-full max-w-2xl rounded-lg shadow-xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button type="button" className="text-gray-500 hover:text-gray-700 text-xl leading-none" onClick={onClose}>×</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  )
}

function QuestionPreviewSummary({ question }) {
  if (question.type === 'select' && Array.isArray(question.options)) {
    return (
      <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
        {question.options.map((o) => (
          <li key={o.value}>{o.label || o.value} <span className="text-gray-400">({o.points ?? 0} pts)</span></li>
        ))}
      </ul>
    )
  }
  if (question.type === 'boolean') {
    return (
      <p className="mt-1 text-xs text-gray-600">
        Yes: {question.points_if_true ?? 0} pts · No: {question.points_if_false ?? 0} pts
      </p>
    )
  }
  return <p className="mt-1 text-xs text-gray-500">Not scored — informational only</p>
}

function QuestionFormFields({
  question,
  onChange,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
}) {
  if (!question) return null
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Question text</label>
        <input
          className="input-field w-full"
          value={question.label || ''}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="e.g. Operating hours per day"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Answer type</label>
          <select
            className="select-field w-full"
            value={question.type}
            onChange={(e) => onChange({ type: e.target.value, typeChange: true })}
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        {isScoredType(question.type) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question weight</label>
            <input
              type="number"
              min={1}
              className="input-field w-full"
              value={question.weight ?? ''}
              placeholder="1"
              onChange={(e) => onChange({
                weight: e.target.value === '' ? undefined : Number(e.target.value),
              })}
            />
            <p className="text-xs text-gray-500 mt-1">Importance within this section</p>
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={!!question.required}
          onChange={(e) => onChange({ required: e.target.checked })}
        />
        Required during assessment
      </label>

      {question.type === 'select' && Array.isArray(question.options) && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Answer options</div>
          <p className="text-xs text-gray-500 mb-2">Higher points = higher risk when that option is chosen.</p>
          <div className="space-y-2">
            {question.options.map((opt) => (
              <div key={opt.value} className="flex items-center gap-2">
                <input
                  className="input-field flex-1 text-sm"
                  value={opt.label || ''}
                  placeholder="Option label"
                  onChange={(e) => onUpdateOption(opt.value, { label: e.target.value })}
                />
                <input
                  type="number"
                  className="input-field w-20 text-sm"
                  value={opt.points ?? 0}
                  onChange={(e) => onUpdateOption(opt.value, { points: Number(e.target.value) })}
                />
                <span className="text-xs text-gray-400">pts</span>
                <button
                  type="button"
                  className="text-red-500 p-1"
                  disabled={question.options.length <= 1}
                  onClick={() => onRemoveOption(opt.value)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="mt-2 text-sm text-psg-teal-600 hover:underline" onClick={onAddOption}>
            + Add option
          </button>
        </div>
      )}

      {question.type === 'boolean' && (
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            Yes points
            <input
              type="number"
              className="input-field w-20"
              value={question.points_if_true ?? 0}
              onChange={(e) => onChange({ points_if_true: Number(e.target.value) })}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            No points
            <input
              type="number"
              className="input-field w-20"
              value={question.points_if_false ?? 0}
              onChange={(e) => onChange({ points_if_false: Number(e.target.value) })}
            />
          </label>
        </div>
      )}
    </div>
  )
}

export default function VenueRiskAssessmentConfiguration() {
  const [state, setState] = useState(null)
  const [draftDef, setDraftDef] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [message, setMessage] = useState(null)
  const [previewResponses, setPreviewResponses] = useState({})

  const [questionModal, setQuestionModal] = useState(null)
  const [sectionModal, setSectionModal] = useState(null)
  const [workingQuestion, setWorkingQuestion] = useState(null)
  const [workingSectionId, setWorkingSectionId] = useState('')
  const [workingSection, setWorkingSection] = useState(null)

  const load = async () => {
    setLoading(true)
    setMessage(null)
    try {
      const r = await authFetch(getLocalApiUrl('/api/governance/risk/templates'))
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'load failed')
      setState(j.data)
      const editDef = j.data?.draft?.definition || j.data?.active?.definition
      setDraftDef(editDef ? JSON.parse(JSON.stringify(editDef)) : null)
      if (editDef) {
        const empty = {}
        for (const dim of editDef.dimensions || []) {
          for (const q of dim.questions || []) {
            if (q.type === 'boolean') empty[q.id] = false
            else if (q.type === 'select' && q.options?.[0]) empty[q.id] = q.options[0].value
            else empty[q.id] = ''
          }
        }
        setPreviewResponses(empty)
      }
    } catch (e) {
      setMessage({ kind: 'error', text: `Could not load templates: ${e.message}` })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const previewScore = useMemo(() => {
    if (!draftDef) return null
    return scoreTemplate(draftDef, previewResponses)
  }, [draftDef, previewResponses])

  const dimensions = draftDef?.dimensions || []

  const getDimension = (dimId) => dimensions.find((d) => d.id === dimId)

  const getQuestion = (dimId, qId) => {
    const dim = getDimension(dimId)
    return dim?.questions?.find((q) => q.id === qId)
  }

  const updateDimension = (dimId, patch) => {
    setDraftDef((prev) => {
      if (!prev) return prev
      let dims = prev.dimensions.map((d) => (d.id === dimId ? { ...d, ...patch } : d))
      if (patch.weight != null) {
        dims = rebalanceDimensionWeights(dims, dimId, patch.weight)
      }
      return { ...prev, dimensions: dims }
    })
  }

  const removeDimension = (dimId) => {
    setDraftDef((prev) => {
      if (!prev) return prev
      return { ...prev, dimensions: prev.dimensions.filter((d) => d.id !== dimId) }
    })
  }

  const removeQuestion = (dimId, qId) => {
    if (!confirm('Remove this question?')) return
    setDraftDef((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        dimensions: prev.dimensions.map((d) => {
          if (d.id !== dimId) return d
          return { ...d, questions: (d.questions || []).filter((q) => q.id !== qId) }
        }),
      }
    })
    setPreviewResponses((p) => {
      const next = { ...p }
      delete next[qId]
      return next
    })
  }

  const openAddQuestion = (prefillDimId = '') => {
    const dimId = prefillDimId || dimensions[0]?.id || ''
    const dim = getDimension(dimId)
    const q = newQuestion(dim || { questions: [] }, 'select')
    setWorkingSectionId(dimId)
    setWorkingQuestion(q)
    setQuestionModal('add')
  }

  const openEditQuestion = (dimId, qId) => {
    const q = getQuestion(dimId, qId)
    if (!q) return
    setWorkingSectionId(dimId)
    setWorkingQuestion(JSON.parse(JSON.stringify(q)))
    setQuestionModal('edit')
  }

  const closeQuestionModal = () => {
    setQuestionModal(null)
    setWorkingQuestion(null)
    setWorkingSectionId('')
  }

  const saveQuestionModal = () => {
    if (!workingQuestion?.label?.trim()) {
      setMessage({ kind: 'error', text: 'Question text is required.' })
      return
    }
    if (!workingSectionId) {
      setMessage({ kind: 'error', text: 'Select a section for this question.' })
      return
    }
    if (workingQuestion.type === 'select' && (!workingQuestion.options?.length)) {
      setMessage({ kind: 'error', text: 'Add at least one answer option.' })
      return
    }
    const qId = workingQuestion.id
    setDraftDef((prev) => {
      if (!prev) return prev
      const dims = prev.dimensions.map((d) => ({
        ...d,
        questions: (d.questions || []).filter((q) => q.id !== qId),
      }))
      return {
        ...prev,
        dimensions: dims.map((d) => {
          if (d.id !== workingSectionId) return d
          return { ...d, questions: [...(d.questions || []), workingQuestion] }
        }),
      }
    })
    if (workingQuestion.type === 'boolean') {
      setPreviewResponses((p) => ({ ...p, [workingQuestion.id]: p[workingQuestion.id] ?? false }))
    } else if (workingQuestion.type === 'select' && workingQuestion.options?.[0]) {
      setPreviewResponses((p) => ({ ...p, [workingQuestion.id]: p[workingQuestion.id] ?? workingQuestion.options[0].value }))
    }
    closeQuestionModal()
    setMessage({ kind: 'info', text: 'Question updated locally — click Save draft to persist.' })
  }

  const patchWorkingQuestion = (patch) => {
    setWorkingQuestion((prev) => {
      if (!prev) return prev
      if (patch.typeChange && patch.type) {
        const dim = getDimension(workingSectionId) || { questions: [] }
        const fresh = newQuestion(dim, patch.type)
        const { typeChange, ...rest } = patch
        return { ...fresh, id: prev.id, label: prev.label, sort_order: prev.sort_order, ...rest }
      }
      const { typeChange, ...rest } = patch
      return { ...prev, ...rest }
    })
  }

  const openEditSection = (dimId) => {
    const dim = getDimension(dimId)
    if (!dim) return
    setWorkingSection({ ...dim })
    setSectionModal('edit')
  }

  const openAddSection = () => {
    if (!draftDef) return
    const dim = newDimension(draftDef, 'New section')
    setWorkingSection(dim)
    setSectionModal('add')
  }

  const closeSectionModal = () => {
    setSectionModal(null)
    setWorkingSection(null)
  }

  const saveSectionModal = () => {
    if (!workingSection?.label?.trim()) {
      setMessage({ kind: 'error', text: 'Section name is required.' })
      return
    }
    if (sectionModal === 'add') {
      setDraftDef((prev) => ({
        ...prev,
        dimensions: [...(prev.dimensions || []), workingSection],
      }))
    } else {
      updateDimension(workingSection.id, {
        label: workingSection.label,
        weight: Number(workingSection.weight) || 0,
      })
    }
    closeSectionModal()
    setMessage({ kind: 'info', text: 'Section updated locally — click Save draft to persist.' })
  }

  const saveDraft = async () => {
    if (!draftDef) return
    const sum = weightSum(draftDef)
    if (sum !== 100) {
      setMessage({ kind: 'error', text: `Section weights must sum to 100% (currently ${sum}%).` })
      return
    }
    setSaving(true)
    setMessage(null)
    try {
      const r = await authFetch(getLocalApiUrl('/api/governance/risk/templates/draft'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ definition: draftDef }),
      })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`)
      setState(j.data)
      const editDef = j.data?.draft?.definition || j.data?.active?.definition
      setDraftDef(editDef ? JSON.parse(JSON.stringify(editDef)) : null)
      setMessage({ kind: 'success', text: 'Draft saved.' })
    } catch (e) {
      setMessage({ kind: 'error', text: `Save failed: ${e.message}` })
    } finally {
      setSaving(false)
    }
  }

  const publish = async () => {
    setPublishing(true)
    setMessage(null)
    try {
      const r = await authFetch(getLocalApiUrl('/api/governance/risk/templates/publish'), { method: 'POST' })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`)
      setState(j.data)
      const editDef = j.data?.draft?.definition || j.data?.active?.definition
      setDraftDef(editDef ? JSON.parse(JSON.stringify(editDef)) : null)
      setMessage({ kind: 'success', text: 'Published — assessments will use these questions.' })
    } catch (e) {
      setMessage({ kind: 'error', text: `Publish failed: ${e.message}` })
    } finally {
      setPublishing(false)
    }
  }

  const resetFromActive = () => {
    if (!state?.active?.definition) return
    setDraftDef(JSON.parse(JSON.stringify(state.active.definition)))
    setMessage({ kind: 'info', text: 'Reset to published template — not saved yet.' })
  }

  const wSum = weightSum(draftDef)

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-6">
      <div className="card p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-indigo-50 text-indigo-700">
            <CogIcon className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900">Risk Assessment Configuration</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage venue ML/TF risk assessment questions, section weights, and scoring. Preview questions below;
              use <strong>Edit</strong> or <strong>Add question</strong> to change them in a dialog.
            </p>
            {state?.active && (
              <p className="text-xs text-gray-500 mt-2">
                Published v{state.active.version}
                {state.draft && <span className="text-amber-700"> · draft v{state.draft.version} not yet published</span>}
              </p>
            )}
          </div>
          <button type="button" onClick={load} className="btn-outline text-sm" disabled={loading}>
            <ArrowPathIcon className="h-4 w-4 inline mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm border ${
          message.kind === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
          : message.kind === 'error' ? 'bg-red-50 border-red-200 text-red-900'
          : 'bg-amber-50 border-amber-200 text-amber-900'
        }`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !draftDef ? (
        <p className="text-sm text-gray-500">No template available.</p>
      ) : (
        <>
          <div className="card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary text-sm inline-flex items-center gap-1"
                  onClick={() => openAddQuestion()}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add question
                </button>
                <button
                  type="button"
                  className="btn-outline text-sm inline-flex items-center gap-1"
                  onClick={openAddSection}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add section
                </button>
              </div>
              <div className={`text-sm font-medium ${wSum === 100 ? 'text-emerald-700' : 'text-red-600'}`}>
                Section weights: {wSum}% {wSum !== 100 && '(must total 100%)'}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Assessment title</label>
                <input
                  className="input-field w-full"
                  value={draftDef.label || ''}
                  onChange={(e) => setDraftDef((d) => ({ ...d, label: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Low risk max (%)</label>
                <input
                  type="number"
                  className="input-field w-full"
                  value={draftDef.bands?.low_max ?? 30}
                  onChange={(e) => setDraftDef((d) => ({
                    ...d,
                    bands: { ...d.bands, low_max: Number(e.target.value) },
                  }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Medium risk max (%)</label>
                <input
                  type="number"
                  className="input-field w-full"
                  value={draftDef.bands?.medium_max ?? 60}
                  onChange={(e) => setDraftDef((d) => ({
                    ...d,
                    bands: { ...d.bands, medium_max: Number(e.target.value) },
                  }))}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {dimensions
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              .map((dim) => (
                <div key={dim.id} className="card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3 border-b pb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{dim.label}</h2>
                      <p className="text-xs text-gray-500">
                        Section weight: {dim.weight ?? 0}% · {(dim.questions || []).length} question(s)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="btn-outline text-xs inline-flex items-center gap-1"
                        onClick={() => openAddQuestion(dim.id)}
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Add question
                      </button>
                      <button
                        type="button"
                        className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                        onClick={() => openEditSection(dim.id)}
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                        Edit section
                      </button>
                    </div>
                  </div>

                  {(dim.questions || []).length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center border border-dashed rounded-lg">
                      No questions in this section yet.{' '}
                      <button type="button" className="text-psg-teal-600 hover:underline" onClick={() => openAddQuestion(dim.id)}>
                        Add a question
                      </button>
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {(dim.questions || [])
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .map((q, idx) => (
                          <div
                            key={q.id}
                            className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-gray-50 hover:bg-gray-100/80 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs text-gray-400 font-mono">Q{idx + 1}</span>
                                <span className="font-medium text-gray-900 text-sm">{q.label}</span>
                                {q.required && (
                                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Required</span>
                                )}
                                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                                  {typeLabel(q.type)}
                                </span>
                                {isScoredType(q.type) && q.weight != null && (
                                  <span className="text-xs text-gray-500">weight {q.weight}</span>
                                )}
                              </div>
                              <QuestionPreviewSummary question={q} />
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                className="p-2 text-gray-600 hover:text-psg-teal rounded"
                                title="Edit question"
                                onClick={() => openEditQuestion(dim.id, q.id)}
                              >
                                <PencilSquareIcon className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                className="p-2 text-gray-400 hover:text-red-600 rounded"
                                title="Remove question"
                                onClick={() => removeQuestion(dim.id, q.id)}
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
          </div>

          {previewScore && (
            <div className="card p-4 border-dashed bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <ChartBarIcon className="h-5 w-5 text-psg-teal" />
                <h2 className="text-sm font-semibold text-gray-900">Score preview</h2>
              </div>
              <p className="text-sm">
                Overall: <strong>{previewScore.overall}%</strong> ({previewScore.band})
              </p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600">
                {dimensions.map((d) => (
                  <span key={d.id}>{d.label}: {previewScore.dimensions?.[d.id] ?? '—'}%</span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={saveDraft}
              disabled={saving || publishing}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-psg-teal text-white rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save draft'}
              <CheckCircleIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={saving || publishing || !state?.draft}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md disabled:opacity-50"
            >
              {publishing ? 'Publishing…' : 'Publish'}
              <CloudArrowUpIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={resetFromActive}
              disabled={saving || publishing || !state?.active}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm text-gray-700 border rounded-md"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Reset from published
            </button>
            <a href="/governance-safe/risk" className="text-sm text-psg-teal-600 hover:underline ml-2">
              Run assessments →
            </a>
          </div>

          {state?.versions?.length > 0 && (
            <div className="card p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Version history</h2>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase border-b">
                    <th className="text-left p-2">Version</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Published</th>
                  </tr>
                </thead>
                <tbody>
                  {state.versions.map((v) => (
                    <tr key={v.id} className="border-b">
                      <td className="p-2">{v.version}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          v.status === 'active' ? 'bg-emerald-100 text-emerald-800'
                          : v.status === 'draft' ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {v.status}
                        </span>
                      </td>
                      <td className="p-2">{v.published_at ? String(v.published_at).slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Modal
        open={questionModal != null}
        onClose={closeQuestionModal}
        title={questionModal === 'add' ? 'Add risk assessment question' : 'Edit risk assessment question'}
        footer={(
          <>
            <button type="button" className="btn-outline text-sm" onClick={closeQuestionModal}>Cancel</button>
            <button type="button" className="btn-primary text-sm" onClick={saveQuestionModal}>
              {questionModal === 'add' ? 'Add question' : 'Save changes'}
            </button>
          </>
        )}
      >
        {workingQuestion && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
              <select
                className="select-field w-full"
                value={workingSectionId}
                onChange={(e) => setWorkingSectionId(e.target.value)}
              >
                {dimensions.map((d) => (
                  <option key={d.id} value={d.id}>{d.label} ({d.weight}%)</option>
                ))}
              </select>
            </div>
            <QuestionFormFields
              question={workingQuestion}
              onChange={patchWorkingQuestion}
              onAddOption={() => setWorkingQuestion((q) => ({
                ...q,
                options: [...(q.options || []), newSelectOption(q)],
              }))}
              onUpdateOption={(optValue, patch) => setWorkingQuestion((q) => ({
                ...q,
                options: (q.options || []).map((o) => (o.value === optValue ? { ...o, ...patch } : o)),
              }))}
              onRemoveOption={(optValue) => setWorkingQuestion((q) => ({
                ...q,
                options: (q.options || []).filter((o) => o.value !== optValue),
              }))}
            />
          </div>
        )}
      </Modal>

      <Modal
        open={sectionModal != null}
        onClose={closeSectionModal}
        title={sectionModal === 'add' ? 'Add section' : 'Edit section'}
        footer={(
          <>
            <button type="button" className="btn-outline text-sm" onClick={closeSectionModal}>Cancel</button>
            {sectionModal === 'edit' && (
              <button
                type="button"
                className="text-sm text-red-600 px-3 py-2"
                onClick={() => {
                  if (confirm('Remove this section and all its questions?')) {
                    removeDimension(workingSection.id)
                    closeSectionModal()
                  }
                }}
              >
                Delete section
              </button>
            )}
            <button type="button" className="btn-primary text-sm" onClick={saveSectionModal}>Save section</button>
          </>
        )}
      >
        {workingSection && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section name</label>
              <input
                className="input-field w-full"
                value={workingSection.label || ''}
                onChange={(e) => setWorkingSection((s) => ({ ...s, label: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section weight (% of overall)</label>
              <input
                type="number"
                min={0}
                max={100}
                className="input-field w-full"
                value={workingSection.weight ?? 0}
                onChange={(e) => setWorkingSection((s) => ({ ...s, weight: Number(e.target.value) }))}
              />
              <p className="text-xs text-gray-500 mt-1">All section weights must total 100% before saving draft.</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
